import { GamePlayConfig, QueryResponse } from 'shared-services'
import { GamePlay, GamePlayGame } from '../models/game-play'
import { GamePlayDto } from '../dtos/game-play.dto'
import { PlayerScoreCard } from '../models/player-score-card'
import { GamePlayErrorCode } from '../error-codes/game-play.error-code'
import { PlayerGameResult } from '../models/player-game-result'
import { PlayerGameResultsErrorCode } from '../error-codes/player-game-results.error-code'
import { PlayerScoreCardsErrorCode } from '../error-codes/player-score-cards.error-code'

export class GamePlayService {
  public constructor(private readonly gamePlayConfig: GamePlayConfig) {}

  public async findGamePlay(): Promise<GamePlay | undefined> {
    const response = await fetch(
      `${this.gamePlayConfig.apiBaseUrl}/v1/game-play`,
      {
        credentials: 'include',
      },
    )

    const gamePlayResponse = (await response.json()) as QueryResponse<
      GamePlayDto,
      GamePlayErrorCode
    >

    if (!gamePlayResponse.isSuccessful) {
      return undefined
    }

    const gamePlayDto = gamePlayResponse.result
    const gamePlayGameDto = gamePlayResponse.result.currentGame

    return new GamePlay({
      gameShortId: gamePlayDto.gameShortId,
      gamePlayId: gamePlayDto.gamePlayId,
      currentGame: new GamePlayGame(
        gamePlayGameDto.gamePlayId,
        gamePlayGameDto.gameId,
        gamePlayGameDto.gameStatus,
      ),
      status: gamePlayDto.status,
      shouldShowCompletedGame: gamePlayDto.shouldShowCompletedGame,
      user: gamePlayDto.user,
      rightOpponent: gamePlayDto.rightOpponent,
      leftOpponent: gamePlayDto.leftOpponent,
    })
  }

  public async getPlayerScoreCards(
    userIds: string[],
    gamePlayId: string,
  ): Promise<PlayerScoreCard[]> {
    const userIdsQuery = userIds.map((userId) => `userIds=${userId}`).join('&')
    const query = `${userIdsQuery}&gamePlayId=${gamePlayId}`

    const response = await fetch(
      `${this.gamePlayConfig.apiBaseUrl}/v1/player-score-cards?${query}`,
      {
        credentials: 'include',
      },
    )

    const playerScoreCardsResponse = (await response.json()) as QueryResponse<
      PlayerScoreCard[],
      PlayerScoreCardsErrorCode
    >

    if (!playerScoreCardsResponse.isSuccessful) {
      return []
    }

    return playerScoreCardsResponse.result
  }

  public async leaveGamePlay(gamePlayId: string): Promise<void> {
    const body = {
      gamePlayId,
    }

    await fetch(`${this.gamePlayConfig.apiBaseUrl}/v1/leave-game-play`, {
      method: 'POST',
      body: JSON.stringify(body),
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    })
  }

  public async getPlayerGameResults(
    gamePlayId: string,
  ): Promise<PlayerGameResult[]> {
    const query = `gamePlayId=${gamePlayId}`

    const response = await fetch(
      `${this.gamePlayConfig.apiBaseUrl}/v1/player-game-results?${query}`,
      {
        credentials: 'include',
      },
    )

    const gamePlayResponse = (await response.json()) as QueryResponse<
      PlayerGameResult[],
      PlayerGameResultsErrorCode
    >

    if (!gamePlayResponse.isSuccessful) {
      return []
    }

    return gamePlayResponse.result
  }
}
